import { useState, type ReactNode } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ledgerContainerSx } from '../../../shared/components/ledger';

// Harness-only chrome: a resizable frame that owns the CSS container the
// ledger's collapse point queries. Real screens own their own container.

const FRAME_WIDTHS = [
  { key: 'desktop', label: 'Desktop', width: '100%' },
  { key: 'tablet', label: '744px', width: 744 },
  { key: 'phone', label: '390px', width: 390 },
] as const;

type FrameKey = (typeof FRAME_WIDTHS)[number]['key'];

interface ScreenFrameProps {
  title: string;
  subtitle?: string;
  /** CSS container name the ledger inside queries. */
  container: string;
  children: ReactNode;
}

export function ScreenFrame({
  title,
  subtitle,
  container,
  children,
}: ScreenFrameProps) {
  const [frame, setFrame] = useState<FrameKey>('desktop');
  const width = FRAME_WIDTHS.find(f => f.key === frame)?.width ?? '100%';

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={frame}
          onChange={(_, value: FrameKey | null) => {
            if (value) {
              setFrame(value);
            }
          }}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25 } }}
        >
          {FRAME_WIDTHS.map(f => (
            <ToggleButton key={f.key} value={f.key}>
              <Typography variant="caption">{f.label}</Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          width,
          maxWidth: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          ...ledgerContainerSx(container),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
